import React, { useEffect, useState } from 'react';
import axiosInstance from '../api';
import { useLang } from '../i18n/LanguageContext';
import WaterLoader from '../components/WaterLoader';

// Department display order + friendly labels (keys match owned_by on tank_mst).
// `tint` = soft card splash, `bar` = top border, `num` = count colour,
// `head` / `stripe` = tank-list modal header + alternate-row colours.
const DEPT_META = [
  { code: 'WRD',    name: 'Water Resources Department', nameTa: 'நீர்வளத் துறை',              tint: 'bg-sky-50 border-sky-100',         bar: 'border-t-sky-500', num: 'text-sky-700',         badge: 'bg-sky-100 text-sky-800', head: 'bg-sky-600', stripe: 'even:bg-sky-50' },
  { code: 'RDPR',   name: 'Rural Development & Panchayat Raj', nameTa: 'ஊரக வளர்ச்சி மற்றும் ஊராட்சித் துறை',       tint: 'bg-lime-50 border-lime-100',       bar: 'border-t-lime-500', num: 'text-lime-700',       badge: 'bg-lime-100 text-lime-800', head: 'bg-lime-600', stripe: 'even:bg-lime-50' },
  { code: 'DTP',    name: 'Directorate of Town Panchayats', nameTa: 'பேரூராட்சிகள் இயக்குநரகம்',          tint: 'bg-pink-50 border-pink-100',       bar: 'border-t-pink-500', num: 'text-pink-700',       badge: 'bg-pink-100 text-pink-800', head: 'bg-pink-600', stripe: 'even:bg-pink-50' },
  { code: 'GCC',    name: 'Greater Chennai Corporation', nameTa: 'பெருநகர சென்னை மாநகராட்சி',             tint: 'bg-amber-50 border-amber-100',     bar: 'border-t-amber-500', num: 'text-amber-700',     badge: 'bg-amber-100 text-amber-800', head: 'bg-amber-600', stripe: 'even:bg-amber-50' },
  { code: 'DMA',    name: 'Municipal Administration (DMA)', nameTa: 'நகராட்சி நிர்வாக இயக்குநரகம்',          tint: 'bg-violet-50 border-violet-100',   bar: 'border-t-violet-500', num: 'text-violet-700',   badge: 'bg-violet-100 text-violet-800', head: 'bg-violet-600', stripe: 'even:bg-violet-50' },
  { code: 'HRCE',   name: 'Hindu Religious & Charitable Endowments', nameTa: 'இந்து சமய அறநிலையத் துறை', tint: 'bg-orange-50 border-orange-100',   bar: 'border-t-orange-500', num: 'text-orange-700',   badge: 'bg-orange-100 text-orange-800', head: 'bg-orange-600', stripe: 'even:bg-orange-50' },
  { code: 'FOREST', name: 'Environment, Climate Change & Forest', nameTa: 'சுற்றுச்சூழல், காலநிலை மாற்றம் மற்றும் வனத் துறை',    tint: 'bg-emerald-50 border-emerald-100', bar: 'border-t-emerald-500', num: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800', head: 'bg-emerald-600', stripe: 'even:bg-emerald-50' },
];

const DepartmentCards = () => {
  const [countsByDept, setCountsByDept] = useState({});
  const [loading, setLoading] = useState(true);
  const [openDept, setOpenDept] = useState(null);
  const { tr } = useLang();

  useEffect(() => {
    axiosInstance
      .get('/applications/public/portal-stats', { silent: true })
      .then((res) => {
        const rows = res?.data?.data?.tanks?.by_department || [];
        const map = Object.fromEntries(rows.map((r) => [r.department_code, r.tank_count]));
        setCountsByDept(map);
      })
      .catch(() => setCountsByDept({}))
      .finally(() => setLoading(false));
  }, []);

  const formatNumber = (n) => Number(n || 0).toLocaleString('en-IN');

  return (
    <section className="py-10 bg-white">
      <div className="container mx-auto px-8 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-4xl lg:text-5xl font-semibold text-brand-700 mb-4 leading-tight">
            {tr('Stakeholder Departments', 'பங்குதாரர் துறைகள்')}
          </h2>
          <div className="w-16 h-1 bg-brand-accent mx-auto mb-4"></div>
          <p className="text-lg text-slate-600 font-light max-w-3xl mx-auto leading-relaxed">
            {tr('Government departments managing water bodies across Tamil Nadu', 'தமிழ்நாடு முழுவதும் நீர்நிலைகளை நிர்வகிக்கும் அரசுத் துறைகள்')}
          </p>
        </div>

        {/* Department Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {DEPT_META.map((dept) => {
            const count = countsByDept[dept.code];
            return (
              <button
                type="button"
                key={dept.code}
                onClick={() => setOpenDept(dept)}
                className={`${dept.tint} ${dept.bar} text-left border border-t-4 rounded-xl p-5 flex flex-col hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 cursor-pointer`}
              >
                <span className={`self-start px-2 py-0.5 text-xs font-semibold rounded ${dept.badge} mb-3`}>
                  {dept.code}
                </span>
                <h3 className="text-base font-semibold text-slate-800 mb-4 leading-snug flex-grow">
                  {tr(dept.name, dept.nameTa)}
                </h3>
                <div className="pt-3 border-t border-black/5">
                  <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                    {tr('Water Bodies', 'நீர்நிலைகள்')}
                  </div>
                  <div className={`text-3xl font-semibold ${dept.num}`}>
                    {loading ? '—' : formatNumber(count)}
                  </div>
                  <div className="mt-2 text-xs font-medium text-slate-500">{tr('View water bodies →', 'நீர்நிலைகளைக் காண்க →')}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      {openDept && <TankListModal dept={openDept} onClose={() => setOpenDept(null)} />}
    </section>
  );
};

const PAGE_SIZE = 20;

// Public tank list for one department — searchable, paginated.
const TankListModal = ({ dept, onClose }) => {
  const { tr } = useLang();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Debounce typing → query.
  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    setError('');
    axiosInstance
      .get('/tanks/public', { params: { owned_by: dept.code, search: query || undefined, page, pageSize: PAGE_SIZE }, silent: true })
      .then((res) => {
        setRows(res.data?.data || []);
        setPagination(res.data?.pagination || { total: 0, totalPages: 1 });
      })
      .catch(() => { setRows([]); setError(tr('Could not load water bodies. Please try again.', 'நீர்நிலைகளை ஏற்ற முடியவில்லை. மீண்டும் முயற்சிக்கவும்.')); })
      .finally(() => setLoading(false));
  }, [dept.code, query, page]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const start = pagination.total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const end = Math.min(page * PAGE_SIZE, pagination.total);

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-5xl max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className={`${dept.head} px-5 py-4 flex items-start justify-between gap-4`}>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-white/80">{dept.code}</div>
            <h3 className="text-lg font-semibold text-white">{tr(dept.name, dept.nameTa)} — {tr('Water Bodies', 'நீர்நிலைகள்')}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none" aria-label="Close">×</button>
        </div>

        <div className="px-5 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tr('Search by name, district, block, village…', 'பெயர், மாவட்டம், வட்டாரம், கிராமம் மூலம் தேடுக…')}
            className="w-full sm:w-80 border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-brand-600"
          />
          <span className="text-xs text-slate-500">
            {loading ? tr('Loading…', 'ஏற்றப்படுகிறது…') : `${start.toLocaleString('en-IN')}–${end.toLocaleString('en-IN')} / ${pagination.total.toLocaleString('en-IN')}`}
          </span>
        </div>

        <div className="overflow-auto flex-1">
          <table className="w-full text-sm">
            <thead className="sticky top-0">
              <tr className="bg-slate-700 text-white text-left text-xs uppercase tracking-wide">
                <th className="px-4 py-2.5 font-semibold">#</th>
                <th className="px-4 py-2.5 font-semibold">{tr('Water Body', 'நீர்நிலை')}</th>
                <th className="px-4 py-2.5 font-semibold">{tr('Type', 'வகை')}</th>
                <th className="px-4 py-2.5 font-semibold">{tr('District', 'மாவட்டம்')}</th>
                <th className="px-4 py-2.5 font-semibold">{tr('Block / ULB', 'வட்டாரம் / உள்ளாட்சி')}</th>
                <th className="px-4 py-2.5 font-semibold">{tr('Village / Town', 'கிராமம் / நகரம்')}</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.map((t, i) => (
                <tr key={t.tank_id} className={`odd:bg-white ${dept.stripe}`}>
                  <td className="px-4 py-2 text-slate-500">{start + i}</td>
                  <td className="px-4 py-2 font-medium text-slate-900">{t.water_body_name || '—'}</td>
                  <td className="px-4 py-2 text-slate-600">{t.tank_type || '—'}</td>
                  <td className="px-4 py-2 text-slate-700">{t.district_name || '—'}</td>
                  <td className="px-4 py-2 text-slate-700">{t.block_name || t.ulb_name || '—'}</td>
                  <td className="px-4 py-2 text-slate-600">{t.village_or_town_name || '—'}</td>
                </tr>
              ))}
              {!loading && !rows.length && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">{error || tr('No water bodies found.', 'நீர்நிலைகள் எதுவும் இல்லை.')}</td></tr>
              )}
              {loading && (
                <tr><td colSpan={6} className="px-4 py-8"><WaterLoader size={56} label={tr('Loading…', 'ஏற்றப்படுகிறது…')} /></td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between">
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => p - 1)}
            className="px-4 py-1.5 text-sm border border-slate-300 disabled:opacity-40 hover:bg-slate-50"
          >
            ← {tr('Previous', 'முந்தைய')}
          </button>
          <span className="text-sm text-slate-600">{tr('Page', 'பக்கம்')} {page} / {pagination.totalPages}</span>
          <button
            type="button"
            disabled={page >= pagination.totalPages || loading}
            onClick={() => setPage((p) => p + 1)}
            className="px-4 py-1.5 text-sm border border-slate-300 disabled:opacity-40 hover:bg-slate-50"
          >
            {tr('Next', 'அடுத்த')} →
          </button>
        </div>
      </div>
    </div>
  );
};

export default DepartmentCards;
